package br.com.vvrefeicoes.portal.feature.forms

import br.com.vvrefeicoes.portal.domain.model.PermissionAction
import br.com.vvrefeicoes.portal.domain.model.PermissionKeys
import br.com.vvrefeicoes.portal.domain.model.allowsAny
import kotlin.math.roundToInt

enum class FormStatus(
    val label: String,
) {
    DRAFT("Rascunho"),
    PENDING_APPROVAL("Em aprovação"),
    APPROVED("Aprovados"),
    INACTIVE("Inativos"),
    ;

    companion object {
        val visibleOrder =
            listOf(
                DRAFT,
                PENDING_APPROVAL,
                APPROVED,
                INACTIVE,
            )
    }
}

fun FormStatus.opensInReadMode(): Boolean = this != FormStatus.DRAFT

enum class FormQuestionType(
    val label: String,
) {
    TEXT("Texto"),
    SINGLE_CHOICE("Seleção única"),
    MULTIPLE_CHOICE("Seleção múltipla"),
    FILE("Arquivo"),
}

enum class FormFileType(
    val label: String,
) {
    IMAGE("Imagens"),
    PDF("PDF"),
}

enum class FormItemType {
    TEXT,
    QUESTION,
    CALCULATION_TABLE,
    QUESTION_TABLE,
    QUESTION_MATRIX,
    TEXT_TABLE,
}

data class FormSummary(
    val id: String,
    val title: String,
    val description: String,
    val status: FormStatus,
    val sectors: List<String>,
    val version: Int,
    val createdBy: String,
    val updatedAt: String,
    val approvedBy: String? = null,
    val obsolete: Boolean = false,
)

data class FormSignature(
    val id: String,
    val label: String,
)

data class FormDraft(
    val title: String,
    val description: String,
    val sectors: List<String>,
    val sections: List<FormSection>,
    val signatures: List<FormSignature> = emptyList(),
)

data class FormDefinition(
    val id: String,
    val familyId: String,
    val title: String,
    val description: String,
    val sectors: List<String>,
    val status: FormStatus,
    val version: Int,
    val createdBy: String,
    val updatedAt: String,
    val approvedBy: String? = null,
    val rejectionReason: String? = null,
    val obsolete: Boolean = false,
    val sections: List<FormSection>,
    val signatures: List<FormSignature> = emptyList(),
) {
    fun toSummary() =
        FormSummary(
            id = id,
            title = title,
            description = description,
            status = status,
            sectors = sectors,
            version = version,
            createdBy = createdBy,
            updatedAt = updatedAt,
            approvedBy = approvedBy,
            obsolete = obsolete,
        )
}

data class FormSection(
    val id: String,
    val title: String,
    val items: List<FormItem> = emptyList(),
)

data class FormItem(
    val id: String,
    val type: FormItemType,
    val textHtml: String = "",
    val question: FormQuestion? = null,
    val choiceTable: FormChoiceTable? = null,
    val questionTable: FormQuestionTable? = null,
    val questionMatrix: FormQuestionMatrix? = null,
    val textTable: FormTextTable? = null,
)

data class FormChoiceTable(
    val id: String,
    val title: String,
    val columns: List<FormChoiceColumn> = emptyList(),
    val questions: List<FormChoiceQuestion> = emptyList(),
)

data class FormChoiceColumn(
    val id: String,
    val label: String,
    val weight: Double? = null,
)

data class FormChoiceQuestion(
    val id: String,
    val title: String,
    val weight: Int? = null,
)

data class FormQuestionMatrix(
    val id: String,
    val title: String,
    val columns: List<FormQuestionMatrixColumn> = emptyList(),
    val rows: List<FormQuestionMatrixRow> = emptyList(),
)

data class FormQuestionMatrixColumn(
    val id: String,
    val label: String,
)

data class FormQuestionMatrixRow(
    val id: String,
    val label: String,
)

data class FormQuestionTable(
    val id: String,
    val title: String,
    val rowCount: Int = 2,
    val columnCount: Int = 1,
    val headers: List<String> = listOf(""),
)

fun FormQuestionTable.header(
    column: Int,
): String = headers.getOrElse(column) { "" }

fun FormQuestionTable.withDimensions(
    rows: Int,
    columns: Int,
): FormQuestionTable {
    val targetRows = rows.coerceAtLeast(2)
    val targetColumns = columns.coerceAtLeast(1)
    val newHeaders =
        List(targetColumns) { column ->
            headers.getOrElse(column) { "" }
        }
    return copy(
        rowCount = targetRows,
        columnCount = targetColumns,
        headers = newHeaders,
    )
}

fun FormQuestionTable.withHeader(
    column: Int,
    value: String,
): FormQuestionTable {
    if (column !in 0 until columnCount) return this
    val updated = headers.toMutableList()
    while (updated.size <= column) updated += ""
    updated[column] = value
    return copy(headers = updated)
}

data class FormTextTable(
    val id: String,
    val title: String,
    val rowCount: Int = 1,
    val columnCount: Int = 1,
    val cells: List<String> = listOf(""),
)

fun FormTextTable.cell(
    row: Int,
    column: Int,
): String =
    if (row in 0 until rowCount && column in 0 until columnCount) {
        cells.getOrElse(row * columnCount + column) { "" }
    } else {
        ""
    }

fun FormTextTable.withDimensions(
    rows: Int,
    columns: Int,
): FormTextTable {
    val targetRows = rows.coerceAtLeast(1)
    val targetColumns = columns.coerceAtLeast(1)
    val newCells =
        List(targetRows * targetColumns) { index ->
            val row = index / targetColumns
            val column = index % targetColumns
            if (row < rowCount && column < columnCount) {
                cells.getOrElse(row * columnCount + column) { "" }
            } else {
                ""
            }
        }
    return copy(
        rowCount = targetRows,
        columnCount = targetColumns,
        cells = newCells,
    )
}

fun FormTextTable.withCell(
    row: Int,
    column: Int,
    value: String,
): FormTextTable {
    if (row !in 0 until rowCount || column !in 0 until columnCount) return this
    val index = row * columnCount + column
    val updated = cells.toMutableList()
    while (updated.size <= index) updated += ""
    updated[index] = value
    return copy(cells = updated)
}

data class FormQuestion(
    val id: String,
    val title: String,
    val type: FormQuestionType,
    val required: Boolean = false,
    val options: List<FormOption> = emptyList(),
    val weight: Int? = null,
    val acceptedFiles: Set<FormFileType> = setOf(FormFileType.IMAGE, FormFileType.PDF),
)

data class FormOption(
    val id: String,
    val label: String,
    val weight: Int? = null,
)

enum class FormSector(
    val label: String,
    val permissionKey: String,
) {
    DHO("DHO", PermissionKeys.FORMULARIOS_DHO),
    SGI("SGI", PermissionKeys.FORMULARIOS_SGI),
    OUVIDORIA("Ouvidoria", PermissionKeys.FORMULARIOS_OUVIDORIA),
    ;

    companion object {
        fun fromLabel(label: String): FormSector? = entries.firstOrNull { it.label == label }
    }
}

val formSectorOptions = FormSector.entries.map { it.label }

fun allowedFormSectors(permissions: Map<String, Set<PermissionAction>>): List<FormSector> = FormSector.entries.filter { permissions.allowsAny(it.permissionKey) }

fun List<String>.hasAllowedFormSector(permissions: Map<String, Set<PermissionAction>>): Boolean = any { label -> FormSector.fromLabel(label)?.let { permissions.allowsAny(it.permissionKey) } == true }

fun List<String>.hasAllFormSectors(permissions: Map<String, Set<PermissionAction>>): Boolean = isNotEmpty() && all { label -> FormSector.fromLabel(label)?.let { permissions.allowsAny(it.permissionKey) } == true }

fun formUpdatedAtLabel(updatedAt: String): String =
    if (updatedAt == "agora") {
        "Última alteração agora"
    } else {
        "Última alteração em $updatedAt"
    }

fun calculateCalculationTableScore(
    table: FormChoiceTable,
    selectedColumnIds: Map<String, String?>,
): Int? {
    var weightedSum = 0.0
    var totalQuestionWeight = 0.0
    table.questions.forEach { question ->
        val questionWeight = question.weight ?: return@forEach
        val columnId = selectedColumnIds[question.id] ?: return@forEach
        val columnWeight = table.columns.firstOrNull { it.id == columnId }?.weight ?: return@forEach
        weightedSum += questionWeight * columnWeight
        totalQuestionWeight += questionWeight
    }
    if (totalQuestionWeight <= 0.0) return null
    return (weightedSum / totalQuestionWeight).roundToInt()
}
